
import React, { useState, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import UserManagement from './pages/UserManagement';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import ChallengePlans from './pages/ChallengePlans';
import Trade from './pages/Trade';
import TraderDashboardLayout from './components/TraderDashboardLayout';
import TermsOfUse from './pages/TermsOfUse';
import ContactUs from './pages/AboutUs';
import FAQ from './pages/FAQ';
import SignUp from './pages/SignUp';
import SignIn from './pages/SignIn';
import TraderSummary from './pages/TraderSummary';
import HowToTrade from './pages/HowToTrade';
import Rules from './pages/Rules';
import PlaceTrades from './pages/PlaceTrades';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';

function App() {
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const theme = useMemo(() => createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#3b82f6', // Blue matching screenshot
        dark: '#2f72e0', // Darker Blue
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#c8384f', // Red matching screenshot
        light: '#d14a5f',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#c8384f',
      },
      background: {
        default: '#131722', // Exness dark background
        paper: '#1e222d', // Card backgrounds
      },
      text: {
        primary: '#d1d4dc',
        secondary: '#787b86', // Gray text
      },
      divider: '#2a2e39',
      action: {
        hover: '#2f72e0',
        selected: '#3b82f6',
      },
    },
    typography: {
      fontFamily: 'Roboto, Arial, sans-serif',
      h1: { fontWeight: 700, color: '#FFFFFF' },
      h2: { fontWeight: 700, color: '#FFFFFF' },
      h3: { fontWeight: 700, color: '#FFFFFF' },
      button: { textTransform: 'none', fontWeight: 700, borderRadius: 8, color: '#FFFFFF' },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 700,
            paddingTop: 12,
            paddingBottom: 12,
            fontSize: 16,
            letterSpacing: 0.5,
            background: 'linear-gradient(135deg, #162447 0%, #1f4068 100%)',
            color: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(22,36,71,0.20)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1f4068 0%, #162447 100%)',
              color: '#FFFFFF',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            background: '#1E293B',
            color: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(34,197,94,0.03)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            background: '#1E293B',
            color: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(34,197,94,0.06)',
            padding: 8,
          },
        },
      },
    },
  }), []);
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Navbar />
            <Routes>
              <Route path="/" element={<Home selectedSymbol={selectedSymbol} />} />
              <Route path="/challenge-plans" element={<ChallengePlans />} />
              <Route path="/trade" element={<TraderDashboardLayout><Trade /></TraderDashboardLayout>} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/trader-summary" element={<TraderDashboardLayout><TraderSummary /></TraderDashboardLayout>} />
              <Route path="/how-to-trade" element={<HowToTrade />} />
              <Route path="/rules" element={<Rules />} />
              <Route path="/place-trades" element={<PlaceTrades />} />
              <Route path="/usermanagement" element={<TraderDashboardLayout><UserManagement /></TraderDashboardLayout>} />
            </Routes>
          </div>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
